import { EventHighlightsReturn, Highlights, HighlightsReturn, SmoothHighlightsReturn } from './types';
import { fetchAllHighlights, fetchEventHighlights } from './services';

import { QUERY_KEYS_NO_FETCHING as QUERY_KEYS } from 'utils/constants';
import { getDays } from './utils';
import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

export function useHighlights(id: string, smoothData: true): SmoothHighlightsReturn;
export function useHighlights(id: string, smoothData?: false): EventHighlightsReturn;
export function useHighlights(): HighlightsReturn;
export function useHighlights(id?: string, smoothData?: boolean) {
	const {
		data: highlights,
		isLoading: isAllLoading,
		isFetching: isAllFetching,
	} = useQuery({
		queryKey: [QUERY_KEYS.HIGHLIGHTS],
		queryFn: fetchAllHighlights,
		enabled: id === undefined,
	});

	// Stable select identity: react-query re-runs an inline select on every render (and getDays
	// returns a fresh object), which would change `eventHighlights` identity each render and defeat
	// every downstream useMemo/React.memo. useCallback pins it so the cached transform is reused.
	const select = useCallback(
		(data: Highlights<string>) => (smoothData ? (getDays(data) as Highlights<Date>) : data),
		[smoothData]
	);

	const {
		data: eventHighlights,
		isLoading: isEventLoading,
		isFetching: isEventFetching,
	} = useQuery({
		queryKey: [QUERY_KEYS.EVENT_HIGHLIGHTS, id],
		queryFn: (): Promise<Highlights<string>> => fetchEventHighlights(id),
		enabled: Boolean(id),
		select,
	});

	// isLoading = initial load only; isFetching = any fetch (incl. a cache-warm refetch). Sections blank on
	// isLoading so a refetch never unmounts a rendered chart; the Refresh button / stat tiles read isFetching.
	// Full rationale (the blink-loop this split fixes) lives in useHighlights/CLAUDE.md — keep it there, not here.
	return {
		highlights,
		eventHighlights,
		isLoading: id ? isEventLoading : isAllLoading,
		isFetching: id ? isEventFetching : isAllFetching,
	};
}
